import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Avatar,
  Grid,
  Container,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SummarizeIcon from "@mui/icons-material/Summarize";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// ─── HELPERS ────────────────────────────────────────────────────────────────
const fmt = (n) =>
  n?.toLocaleString("en-KE", { minimumFractionDigits: 0 }) ?? "—";

// ─── DETECT RESPONSE TYPE ───────────────────────────────────────────────────
const detectResponseType = (data) => {
  if (!data) return "empty";
  if (data.content && typeof data.content === "string") return "text";
  if (data.result?.narrative || data.narrative) return "structured";
  if (data.credit_score || data.customer_name) return "structured";
  return "unknown";
};

const parseStructuredData = (data) => {
  if (data?.result?.narrative) return data.result.narrative;
  if (data?.narrative) return data.narrative;
  return data;
};

const parseCreditRecommendation = (cr) => {
  if (!cr) return null;
  if (typeof cr === "string") {
    try { return JSON.parse(cr); } catch { return null; }
  }
  return cr;
};

// ─── RISK / SCORE COLOR TOKENS ───────────────────────────────────────────────
// Returns MUI theme-path strings safe for sx `color:` props
const RISK_TOKENS = {
  low:    { color: "#2E7D32", colorDark: "#66BB6A", bg: "#E8F5E9", bgDark: "#1B5E2033", border: "#A5D6A7", borderDark: "#388E3C" },
  medium: { color: "#E65100", colorDark: "#FFA726", bg: "#FFF3E0", bgDark: "#BF360C33", border: "#FFCC80", borderDark: "#F57C00" },
  high:   { color: "#B71C1C", colorDark: "#EF5350", bg: "#FFEBEE", bgDark: "#B71C1C33", border: "#EF9A9A", borderDark: "#C62828" },
  unknown:{ color: "#37474F", colorDark: "#90A4AE", bg: "#ECEFF1", bgDark: "#37474F33", border: "#B0BEC5", borderDark: "#546E7A" },
};

const getRiskTokens = (level, isDark) => {
  const key = (level || "").toLowerCase();
  const t = RISK_TOKENS[key] || RISK_TOKENS.unknown;
  return {
    color:  isDark ? t.colorDark  : t.color,
    bg:     isDark ? t.bgDark     : t.bg,
    border: isDark ? t.borderDark : t.border,
  };
};

const SCORE_TOKENS = [
  { min: 700, color: "#2E7D32", colorDark: "#66BB6A", label: "Excellent" },
  { min: 550, color: "#E65100", colorDark: "#FFA726", label: "Fair" },
  { min: 400, color: "#BF360C", colorDark: "#FF7043", label: "Poor" },
  { min: 0,   color: "#B71C1C", colorDark: "#EF5350", label: "Very Poor" },
];

const getScoreToken = (score, isDark) => {
  if (score == null) return { color: isDark ? "#90A4AE" : "#546E7A", label: "N/A" };
  const t = SCORE_TOKENS.find((s) => score >= s.min) || SCORE_TOKENS[3];
  return { color: isDark ? t.colorDark : t.color, label: t.label };
};

const SPENDING_COLORS_LIGHT = ["#1565C0","#2E7D32","#E65100","#B71C1C","#6A1B9A","#00695C"];
const SPENDING_COLORS_DARK  = ["#64B5F6","#81C784","#FFB74D","#E57373","#CE93D8","#4DB6AC"];

// ─── PDF GENERATION ──────────────────────────────────────────────────────────
const generatePDF = async (narrative, creditRec, statementData) => {
  // Dynamically import jsPDF — falls back to text if unavailable
  let jsPDF;
  try {
    const mod = await import("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
    jsPDF = mod?.jsPDF || window?.jspdf?.jsPDF;
  } catch (_) {}

  const n = narrative;
  const cr = creditRec;

  if (!jsPDF) {
    // Fallback: rich text download
    const lines = buildTextReport(n, cr, statementData);
    const blob = new Blob([lines], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `narrative-${statementData?.id || "report"}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return;
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const PW = 210; // page width mm
  const ML = 18, MR = 18; // margins
  const CW = PW - ML - MR;
  let y = 0;

  // ── helpers ──
  const COLORS = {
    primary:   [21, 101, 192],
    dark:      [30,  40,  50],
    medium:    [90, 100, 110],
    light:     [140,150,160],
    divider:   [220,224,228],
    successBg: [232,245,233],
    successFg: [46, 125, 50],
    warnBg:    [255,243,224],
    warnFg:    [230,81,  0],
    errorBg:   [255,235,238],
    errorFg:   [183,28, 28],
    pageBg:    [250,251,252],
  };

  const setFont = (style = "normal", size = 10, color = COLORS.dark) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  const checkPage = (needed = 10) => {
    if (y + needed > 275) {
      doc.addPage();
      y = 16;
    }
  };

  const hRule = (thick = false) => {
    doc.setDrawColor(...COLORS.divider);
    doc.setLineWidth(thick ? 0.5 : 0.2);
    doc.line(ML, y, PW - MR, y);
    y += 4;
  };

  const sectionHeader = (title) => {
    checkPage(14);
    doc.setFillColor(...COLORS.primary);
    doc.roundedRect(ML, y, CW, 9, 1.5, 1.5, "F");
    setFont("bold", 10, [255, 255, 255]);
    doc.text(title, ML + 4, y + 6);
    y += 13;
  };

  const infoRow = (label, value, highlight = false) => {
    checkPage(8);
    setFont("normal", 9, COLORS.medium);
    doc.text(label, ML, y);
    setFont(highlight ? "bold" : "normal", 9, highlight ? COLORS.primary : COLORS.dark);
    doc.text(String(value ?? "—"), ML + CW, y, { align: "right" });
    doc.setDrawColor(...COLORS.divider);
    doc.setLineWidth(0.15);
    doc.line(ML, y + 2, PW - MR, y + 2);
    y += 7;
  };

  const alertBox = (text, type = "warning") => {
    checkPage(16);
    const bg = type === "success" ? COLORS.successBg : type === "error" ? COLORS.errorBg : COLORS.warnBg;
    const fg = type === "success" ? COLORS.successFg : type === "error" ? COLORS.errorFg : COLORS.warnFg;
    const lines = doc.splitTextToSize(text, CW - 10);
    const h = lines.length * 5 + 6;
    doc.setFillColor(...bg);
    doc.roundedRect(ML, y, CW, h, 1.5, 1.5, "F");
    doc.setDrawColor(...fg);
    doc.setLineWidth(0.3);
    doc.line(ML + 1, y + 1, ML + 1, y + h - 1);
    setFont("normal", 8.5, fg);
    doc.text(lines, ML + 5, y + 5);
    y += h + 3;
  };

  const metricBox = (title, value, subtitle, x, w, fgColor = COLORS.primary) => {
    checkPage(22);
    doc.setFillColor(245, 248, 252);
    doc.roundedRect(x, y, w, 20, 2, 2, "F");
    doc.setDrawColor(...COLORS.divider);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, y, w, 20, 2, 2, "S");
    setFont("bold", 7, COLORS.medium);
    doc.text(title.toUpperCase(), x + w / 2, y + 5, { align: "center" });
    // Auto-scale font size for long values to prevent spillage
    const valStr = String(value);
    let fontSize = 14;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(fontSize);
    while (doc.getTextWidth(valStr) > w - 6 && fontSize > 8) {
      fontSize -= 1;
      doc.setFontSize(fontSize);
    }
    doc.setTextColor(...fgColor);
    doc.text(valStr, x + w / 2, y + 13, { align: "center" });
    if (subtitle) {
      setFont("normal", 7, COLORS.light);
      doc.text(subtitle, x + w / 2, y + 18, { align: "center" });
    }
  };

  // ══ COVER / HEADER ══
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, PW, 36, "F");

  setFont("bold", 18, [255, 255, 255]);
  doc.text("FINSIGHTS", ML, 14);
  setFont("normal", 10, [180, 210, 255]);
  doc.text("AI-Generated Narrative Report", ML, 21);

  setFont("normal", 8.5, [200, 220, 255]);
  const rightX = PW - MR;
  doc.text(`Generated: ${new Date().toLocaleString("en-KE")}`, rightX, 14, { align: "right" });
  doc.text(`Statement #${statementData?.id || "—"}`, rightX, 21, { align: "right" });

  y = 42;

  // ── Customer meta bar ──
  doc.setFillColor(...COLORS.pageBg);
  doc.roundedRect(ML, y, CW, 14, 2, 2, "F");
  doc.setDrawColor(...COLORS.divider);
  doc.setLineWidth(0.2);
  doc.roundedRect(ML, y, CW, 14, 2, 2, "S");

  setFont("bold", 10, COLORS.dark);
  doc.text(n?.customer_name || statementData?.customer_name || "Unknown Customer", ML + 4, y + 6);
  setFont("normal", 8.5, COLORS.medium);
  doc.text(`Period: ${n?.statement_period || "N/A"}  •  Months Analysed: ${n?.months_analyzed ?? "—"}`, ML + 4, y + 11);

  y += 20;

  // ── Hero metrics ──
  const mw = (CW - 8) / 3;
  const scoreT = getScoreToken(n?.credit_score, false);
  const riskT  = getRiskTokens(cr?.risk_level, false);
  metricBox("Credit Score", n?.credit_score ?? "—", scoreT.label, ML, mw, scoreT.color);
  metricBox("Risk Level", cr?.risk_level || "—", `Limit: KSh ${fmt(cr?.recommended_loan_limit)}`, ML + mw + 4, mw, riskT.color);
  metricBox("Max Weekly Repayment", `KSh ${fmt(cr?.max_weekly_repayment)}`, "", ML + (mw + 4) * 2, mw, [46, 125, 50]);
  y += 24;

  // ── Income ──
  if (n?.income) {
    sectionHeader("Income Analysis");
    infoRow("Primary Source",    n.income.primary_source   || "—");
    infoRow("Verified Income",   `KSh ${fmt(n.income.verified_income)}`);
    infoRow("Secondary Income",  `KSh ${fmt(n.income.secondary_income)}`);
    infoRow("Monthly Average",   `KSh ${fmt(n.income.monthly_average)}`, true);
    infoRow("Regularity",        n.income.regularity       || "—");
    y += 2;
  }

  // ── Spending ──
  if (n?.spending) {
    sectionHeader("Spending Profile");
    infoRow("Total Monthly Average", `KSh ${fmt(n.spending.total_monthly_average)}`, true);
    if (n.spending.top_categories?.length) {
      checkPage(10);
      setFont("bold", 8, COLORS.medium);
      doc.text("SPENDING BREAKDOWN", ML, y);
      y += 5;
      n.spending.top_categories.forEach((cat) => {
        checkPage(9);
        const barW = CW - 70;
        const fillW = ((cat.percentage || 0) / 100) * barW;
        setFont("normal", 8.5, COLORS.dark);
        doc.text(cat.category, ML, y);
        doc.setFillColor(...COLORS.divider);
        doc.roundedRect(ML + 52, y - 4, barW, 5, 1, 1, "F");
        doc.setFillColor(...COLORS.primary);
        if (fillW > 0) doc.roundedRect(ML + 52, y - 4, fillW, 5, 1, 1, "F");
        setFont("normal", 8, COLORS.medium);
        doc.text(`KSh ${fmt(cat.monthly_avg)} (${cat.percentage?.toFixed(1)}%)`, PW - MR, y, { align: "right" });
        y += 7;
      });
    }
    if (n.spending.fixed_obligations?.length) {
      checkPage(10);
      setFont("bold", 8, COLORS.medium);
      doc.text("FIXED OBLIGATIONS", ML, y);
      y += 5;
      n.spending.fixed_obligations.forEach((o) => {
        infoRow(`${o.description} (${o.frequency})`, `KSh ${fmt(o.amount)}`);
      });
    }
    y += 2;
  }

  // ── Debt ──
  if (n?.debt_profile) {
    sectionHeader("Debt Profile");
    infoRow("Fuliza Usage Count",       n.debt_profile.fuliza_usage_count ?? "—");
    infoRow("Fuliza Average Amount",    `KSh ${fmt(n.debt_profile.fuliza_avg_amount)}`);
    infoRow("Fuliza Repayment",         n.debt_profile.fuliza_repayment_behavior || "—");
    infoRow("Other Loan Repayments",    `KSh ${fmt(n.debt_profile.other_loan_repayments)}`, true);
    checkPage(10);
    setFont("bold", 8, COLORS.medium);
    doc.text("DIGITAL LENDERS DETECTED", ML, y);
    y += 5;
    const lenders = n.debt_profile.digital_lenders_detected;
    if (!lenders?.length) {
      alertBox("None detected — no active mobile loan obligations. This is a positive indicator.", "success");
    } else {
      alertBox(`Detected lenders: ${lenders.join(", ")}`, "warning");
    }
    y += 2;
  }

  // ── Recommendation ──
  if (cr) {
    sectionHeader("Credit Recommendation");
    infoRow("Recommended Loan Limit", `KSh ${fmt(cr.recommended_loan_limit)}`, true);
    infoRow("Risk Level",             cr.risk_level || "—");
    infoRow("Max Weekly Repayment",   `KSh ${fmt(cr.max_weekly_repayment)}`);

    if (cr.installment_options?.length) {
      checkPage(14);
      setFont("bold", 8, COLORS.medium);
      doc.text("INSTALLMENT OPTIONS", ML, y);
      y += 5;
      // Table header - adjusted column widths for better fit
      const colX = [ML, ML + 38, ML + 80, ML + 118, ML + CW];
      const headers = ["Term", "Loan Amount", "Weekly", "Total Repayable"];
      doc.setFillColor(235, 240, 248);
      doc.rect(ML, y - 4, CW, 7, "F");
      setFont("bold", 8, COLORS.primary);
      headers.forEach((h, i) => {
        const align = i === 0 ? "left" : "right";
        const x = i === 0 ? colX[0] : colX[i + 1];
        doc.text(h, x, y, { align });
      });
      y += 4;
      cr.installment_options.forEach((opt, i) => {
        checkPage(8);
        if (i % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(ML, y - 4, CW, 7, "F");
        }
        setFont("normal", 8.5, COLORS.dark);
        doc.text(opt.term_label || "—", colX[0], y);
        setFont("normal", 8.5, COLORS.primary);
        doc.text(`KSh ${fmt(opt.loan_amount)}`, colX[2], y, { align: "right" });
        setFont("normal", 8.5, COLORS.dark);
        doc.text(`KSh ${fmt(opt.weekly_installment)}`, colX[3], y, { align: "right" });
        setFont("bold", 8.5, COLORS.successFg);
        doc.text(`KSh ${fmt(opt.total_repayable)}`, colX[4], y, { align: "right" });
        y += 7;
      });
    }

    if (cr.reasoning) {
      checkPage(20);
      setFont("bold", 8, COLORS.medium);
      doc.text("REASONING", ML, y);
      y += 5;
      const lines = doc.splitTextToSize(cr.reasoning, CW);
      const h = lines.length * 4.8 + 6;
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(ML, y, CW, h, 1.5, 1.5, "F");
      doc.setDrawColor(...COLORS.divider);
      doc.setLineWidth(0.2);
      doc.roundedRect(ML, y, CW, h, 1.5, 1.5, "S");
      setFont("normal", 8.5, COLORS.medium);
      doc.text(lines, ML + 4, y + 5);
      y += h + 4;
    }
  }

  // ── Risk Flags ──
  if (n?.risk_flags?.length) {
    sectionHeader("Risk Flags");
    n.risk_flags.forEach((f) => alertBox(f, "error"));
    y += 2;
  }

  // ── Positive Signals ──
  if (n?.positive_signals?.length) {
    sectionHeader("Positive Signals");
    n.positive_signals.forEach((s) => alertBox(s, "success"));
    y += 2;
  }

  // ── Summary ──
  if (n?.summary_narrative) {
    sectionHeader("Executive Summary");
    checkPage(20);
    const lines = doc.splitTextToSize(n.summary_narrative, CW);
    const h = lines.length * 5 + 8;
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(ML, y, CW, h, 2, 2, "F");
    setFont("normal", 9, COLORS.dark);
    doc.text(lines, ML + 4, y + 6);
    y += h + 4;
  }

  // ── Footer on every page ──
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, 287, PW, 10, "F");
    setFont("normal", 7.5, [200, 220, 255]);
    doc.text("Generated by Finsights AI  •  For loan officer review only  •  Confidential", PW / 2, 293, { align: "center" });
    setFont("normal", 7.5, [200, 220, 255]);
    doc.text(`Page ${p} of ${pageCount}`, PW - MR, 293, { align: "right" });
  }

  doc.save(`finsights-narrative-${statementData?.id || "report"}.pdf`);
};

const buildTextReport = (narrative, creditRec, stmtData) => {
  const n = narrative;
  const cr = creditRec;
  const lines = [];
  lines.push("╔══════════════════════════════════════════════════════════════╗");
  lines.push("║           FINSIGHTS GENERATED NARRATIVE REPORT               ║");
  lines.push("╚══════════════════════════════════════════════════════════════╝");
  lines.push("");
  lines.push(`Customer:     ${n?.customer_name || stmtData?.customer_name || "N/A"}`);
  lines.push(`Statement ID: #${stmtData?.id || stmtData?.statementid || "N/A"}`);
  lines.push(`Period:       ${n?.statement_period || "N/A"}`);
  lines.push(`Generated:    ${new Date().toLocaleString("en-KE")}`);
  lines.push("");
  if (n?.credit_score) {
    lines.push("─── CREDIT SCORE ───────────────────────────────────────────────");
    lines.push(`Score: ${n.credit_score} / 850  |  Band: ${n.credit_score_band || "N/A"}`);
    lines.push("");
  }
  if (n?.income) {
    lines.push("─── INCOME ─────────────────────────────────────────────────────");
    lines.push(`Primary Source: ${n.income.primary_source || "N/A"}`);
    lines.push(`Verified:       KSh ${fmt(n.income.verified_income)}`);
    lines.push(`Monthly Avg:    KSh ${fmt(n.income.monthly_average)}`);
    lines.push(`Regularity:     ${n.income.regularity || "N/A"}`);
    lines.push("");
  }
  if (cr) {
    lines.push("─── RECOMMENDATION ─────────────────────────────────────────────");
    lines.push(`Risk Level:  ${cr.risk_level || "N/A"}`);
    lines.push(`Loan Limit:  KSh ${fmt(cr.recommended_loan_limit)}`);
    lines.push(`Max Weekly:  KSh ${fmt(cr.max_weekly_repayment)}`);
    if (cr.reasoning) { lines.push(""); lines.push(cr.reasoning); }
    lines.push("");
  }
  if (n?.risk_flags?.length) {
    lines.push("─── RISK FLAGS ─────────────────────────────────────────────────");
    n.risk_flags.forEach((f) => lines.push(`⚠  ${f}`));
    lines.push("");
  }
  if (n?.positive_signals?.length) {
    lines.push("─── POSITIVE SIGNALS ───────────────────────────────────────────");
    n.positive_signals.forEach((s) => lines.push(`✓  ${s}`));
    lines.push("");
  }
  if (n?.summary_narrative) {
    lines.push("─── EXECUTIVE SUMMARY ──────────────────────────────────────────");
    lines.push(n.summary_narrative);
    lines.push("");
  }
  lines.push("════════════════════════════════════════════════════════════════");
  lines.push("Generated by Finsights AI  •  For loan officer review.");
  lines.push("════════════════════════════════════════════════════════════════");
  return lines.join("\n");
};

// ─── SECTION CARD ────────────────────────────────────────────────────────────
const SectionCard = ({ title, children, icon: Icon, accentColor }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: isDark ? 6 : 2 },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: isDark
            ? `rgba(${hexToRgb(accentColor)}, 0.12)`
            : `rgba(${hexToRgb(accentColor)}, 0.06)`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {Icon && (
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: isDark
                ? `rgba(${hexToRgb(accentColor)}, 0.25)`
                : `rgba(${hexToRgb(accentColor)}, 0.15)`,
              color: accentColor,
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Avatar>
        )}
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            // ← explicit theme-safe color — never invisible
            color: "text.primary",
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 3, bgcolor: "background.paper" }}>{children}</Box>
    </Paper>
  );
};

// hex → "r, g, b" helper for rgba()
const hexToRgb = (hex = "#000000") => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
};

// ─── INFO ROW ─────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, highlight = false, suffix = null }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: 1.25,
      borderBottom: "1px solid",
      borderColor: "divider",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography
      sx={{ fontSize: 13.5, color: "text.secondary", fontWeight: 400, lineHeight: 1.5, pr: 2, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Box sx={{ textAlign: "right", maxWidth: "58%" }}>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: highlight ? 700 : 500,
          // highlight uses primary, non-highlight uses text.primary — always readable
          color: highlight ? "#64B5F6" : "text.primary",
          lineHeight: 1.5,
        }}
      >
        {value ?? "—"}
      </Typography>
      {suffix && (
        <Typography sx={{ fontSize: 11.5, color: "text.disabled", mt: 0.25 }}>
          {suffix}
        </Typography>
      )}
    </Box>
  </Box>
);

// ─── METRIC CARD ─────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, color, icon: Icon }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        textAlign: "center",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: isDark ? 6 : 3 },
      }}
    >
      {Icon && (
        <Avatar
          sx={{
            width: 44,
            height: 44,
            mb: 1.5,
            bgcolor: `rgba(${hexToRgb(color)}, ${isDark ? 0.2 : 0.12})`,
            color: color,
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Avatar>
      )}
      <Typography
        sx={{
          fontSize: 10.5,
          // Always use text.disabled — theme-safe
          color: "text.disabled",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontWeight: 600,
          mb: 0.75,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 30,
          fontWeight: 800,
          color: color,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.75, fontWeight: 500 }}>
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
const ProgressBar = ({ label, value, percentage, color }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
        {/* label: text.primary — never invisible */}
        <Typography sx={{ fontSize: 13, color: "text.primary", fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 500 }}>
          KSh {fmt(value)}{" "}
          <Box
            component="span"
            sx={{ color: "text.disabled", fontWeight: 400, fontSize: 11.5 }}
          >
            ({percentage?.toFixed(1)}%)
          </Box>
        </Typography>
      </Box>
      <Box
        sx={{
          height: 7,
          bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)",
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${Math.min(percentage || 0, 100)}%`,
            bgcolor: color,
            borderRadius: 99,
            transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </Box>
    </Box>
  );
};

// ─── ALERT BOX ────────────────────────────────────────────────────────────────
const AlertBox = ({ type, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Explicit fg text colors — no "text.primary" bleed on tinted backgrounds
  const CONFIG = {
    success: {
      bg:     isDark ? "rgba(46,125,50,0.18)"  : "#E8F5E9",
      border: isDark ? "#388E3C"               : "#A5D6A7",
      // theme-safe: MUI maps success.dark to a readable shade in both modes
      textColor: isDark ? "#A5D6A7" : "#1B5E20",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
    },
    warning: {
      bg:     isDark ? "rgba(230,81,0,0.18)"   : "#FFF3E0",
      border: isDark ? "#F57C00"               : "#FFCC80",
      textColor: isDark ? "#FFCC80" : "#BF360C",
      icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
    },
    error: {
      bg:     isDark ? "rgba(183,28,28,0.18)"  : "#FFEBEE",
      border: isDark ? "#C62828"               : "#EF9A9A",
      textColor: isDark ? "#EF9A9A" : "#7F0000",
      icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
    },
  };
  const cfg = CONFIG[type] || CONFIG.warning;

  return (
    <Paper
      elevation={0}
      sx={{
        p: "12px 16px",
        borderRadius: 2,
        bgcolor: cfg.bg,
        border: "1px solid",
        borderColor: cfg.border,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
      }}
    >
      <Box sx={{ color: cfg.textColor, mt: "1px", flexShrink: 0 }}>{cfg.icon}</Box>
      <Typography
        sx={{
          fontSize: 13.5,
          // Direct color — NOT "text.primary" which would be invisible on tinted bg in some themes
          color: cfg.textColor,
          lineHeight: 1.65,
          fontWeight: 400,
        }}
      >
        {children}
      </Typography>
    </Paper>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
const GeneratedNarrativePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const navState = location.state;
  const statementData =
    navState?.statementData ||
    JSON.parse(localStorage.getItem("statementData") || "{}");
  const generatedResult = navState?.generatedResult || null;
  const statementId =
    statementData?.id ||
    statementData?.statementid ||
    statementData?.statement_id;

  useEffect(() => {
    if (generatedResult) { setData(generatedResult); setLoading(false); return; }
    if (!statementId) {
      setError("No statement data available. Please generate a narrative from the statements table.");
      setLoading(false);
      return;
    }
    setError("No generated narrative data found. Please generate from the statements table.");
    setLoading(false);
  }, [generatedResult, statementId]);

  const responseType = detectResponseType(data);
  const n  = responseType === "structured" ? parseStructuredData(data) : null;
  const cr = n ? parseCreditRecommendation(n.credit_recommendation) : null;

  const riskT  = getRiskTokens(cr?.risk_level, isDark);
  const scoreT = getScoreToken(n?.credit_score, isDark);
  const spendColors = isDark ? SPENDING_COLORS_DARK : SPENDING_COLORS_LIGHT;

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await generatePDF(n, cr, statementData);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => window.print();
  const handleBack  = () => navigate(-1);

  const handleCopy = () => {
    if (!data) return;
    const text =
      responseType === "text" ? data.content : JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", flexDirection: "column", gap: 2 }}>
        <CircularProgress size={40} thickness={4} />
        <Typography sx={{ color: "text.secondary", fontSize: 15 }}>
          Loading narrative report…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 3, textTransform: "none", color: "text.secondary", fontWeight: 500 }}
        >
          Back to Statements
        </Button>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: isDark ? "rgba(183,28,28,0.12)" : "#FFEBEE",
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 48, color: "error.main", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{ color: "error.main", fontWeight: 600, mb: 1 }}
          >
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 6 }}>

      {/* ── STICKY HEADER ── */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: 1,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.75,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Left: back + title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton
                onClick={handleBack}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 700,
                    // Always text.primary
                    color: "text.primary",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.25,
                  }}
                >
                  Generated Narrative
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mt: 0.25,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 13 }} />
                    {n?.customer_name ||
                      statementData?.customer_name ||
                      `Statement #${statementId}`}
                  </Typography>
                  {n?.statement_period && (
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: "text.disabled",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <CalendarTodayIcon sx={{ fontSize: 13 }} />
                      {n.statement_period}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right: actions */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                  onClick={handleCopy}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    borderColor: "divider",
                    color: "text.secondary",
                    fontWeight: 500,
                    fontSize: 13,
                    "&:hover": { borderColor: "primary.main", color: "primary.main" },
                  }}
                >
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  downloading ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  )
                }
                onClick={handleDownloadPDF}
                disabled={downloading}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: 13,
                  boxShadow: "none",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                {downloading ? "Generating…" : "Download PDF"}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pt: 3 }}>

        {/* ── TEXT FORMAT ── */}
        {responseType === "text" && data.content && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: isDark ? "#1C1C1E" : "#FEFCF6",
            }}
          >
            <Box
              component="pre"
              sx={{
                m: 0,
                fontFamily: '"SF Mono","Cascadia Code","Roboto Mono",Consolas,monospace',
                fontSize: 13,
                lineHeight: 1.75,
                // Always text.primary — safe in both modes
                color: "text.primary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                letterSpacing: "0.01em",
              }}
            >
              {data.content}
            </Box>
          </Paper>
        )}

        {/* ── STRUCTURED FORMAT ── */}
        {responseType === "structured" && n && (
          <Box>

            {/* ── Hero row ── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Credit Score */}
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Credit Score"
                  value={n.credit_score ?? "—"}
                  subtitle={scoreT.label}
                  color={scoreT.color}
                  icon={AssessmentIcon}
                />
              </Grid>

              {/* Risk Level */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "2px solid",
                    borderColor: riskT.border,
                    bgcolor: riskT.bg,
                    textAlign: "center",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      mb: 1.5,
                      bgcolor: `rgba(${hexToRgb(riskT.color)}, 0.18)`,
                      color: riskT.color,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      // explicit — not text.disabled which might be unreadable on colored bg
                      color: riskT.color,
                      opacity: 0.75,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 700,
                      mb: 0.75,
                    }}
                  >
                    Risk Level
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 30,
                      fontWeight: 800,
                      color: riskT.color,
                      lineHeight: 1.1,
                      textTransform: "capitalize",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {cr?.risk_level || "Unknown"}
                  </Typography>
                  {cr?.recommended_loan_limit && (
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        // text.secondary is theme-safe, readable on both light and dark tinted bgs
                        color: "text.secondary",
                        mt: 0.75,
                        fontWeight: 500,
                      }}
                    >
                      Limit: KSh {fmt(cr.recommended_loan_limit)}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Quick Stats */}
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Months Analysed"
                  value={n.months_analyzed ?? "—"}
                  subtitle={`Inflow: KSh ${fmt(n.income?.monthly_average)} · Spend: KSh ${fmt(n.spending?.total_monthly_average)}`}
                  color={isDark ? "#64B5F6" : "#0277BD"}
                  icon={AccountBalanceWalletIcon}
                />
              </Grid>
            </Grid>

            {/* ── Income ── */}
            {n.income && (
              <SectionCard
                title="Income Analysis"
                icon={TrendingUpIcon}
                accentColor={isDark ? "#66BB6A" : "#2E7D32"}
              >
                <InfoRow label="Primary Source"   value={n.income.primary_source} />
                <InfoRow label="Verified Income"  value={`KSh ${fmt(n.income.verified_income)}`} />
                <InfoRow label="Secondary Income" value={`KSh ${fmt(n.income.secondary_income)}`} />
                <InfoRow label="Monthly Average"  value={`KSh ${fmt(n.income.monthly_average)}`} highlight />
                <InfoRow
                  label="Regularity"
                  value={n.income.regularity}
                  suffix={n.income.regularity === "irregular" ? "Income varies month to month" : null}
                />
                {n.income.income_dates?.length > 0 && (
                  <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        mb: 1.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Income Activity Days
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                        const active = n.income.income_dates.includes(d);
                        return (
                          <Box
                            key={d}
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: active ? 700 : 500,
                              bgcolor: active
                                ? "primary.main"
                                : isDark
                                  ? "rgba(255,255,255,0.07)"
                                  : "rgba(0,0,0,0.05)",
                              // explicit color — not relying on theme fallthrough
                              color: active
                                ? "#ffffff"
                                : "text.disabled",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {d}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </SectionCard>
            )}

            {/* ── Spending ── */}
            {n.spending && (
              <SectionCard
                title="Spending Profile"
                icon={ShoppingCartIcon}
                accentColor={isDark ? "#FFB74D" : "#E65100"}
              >
                <InfoRow
                  label="Total Monthly Average"
                  value={`KSh ${fmt(n.spending.total_monthly_average)}`}
                  highlight
                />
                {n.spending.top_categories?.length > 0 && (
                  <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        mb: 2,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Spending Breakdown
                    </Typography>
                    {n.spending.top_categories.map((cat, i) => (
                      <ProgressBar
                        key={i}
                        label={cat.category}
                        value={cat.monthly_avg}
                        percentage={cat.percentage}
                        color={spendColors[i % spendColors.length]}
                      />
                    ))}
                  </Box>
                )}
                {n.spending.fixed_obligations?.length > 0 && (
                  <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "text.disabled",
                        mb: 1.5,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Fixed Obligations
                    </Typography>
                    {n.spending.fixed_obligations.map((o, i) => (
                      <InfoRow
                        key={i}
                        label={
                          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {o.description}
                            <Chip
                              label={o.frequency}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10.5,
                                // Use explicit bgcolor/color pair — not relying on inheritance
                                bgcolor: isDark ? "rgba(100,181,246,0.15)" : "rgba(21,101,192,0.08)",
                                color: isDark ? "#64B5F6" : "#1565C0",
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                        }
                        value={`KSh ${fmt(o.amount)}`}
                      />
                    ))}
                  </Box>
                )}
              </SectionCard>
            )}

            {/* ── Debt ── */}
            {n.debt_profile && (
              <SectionCard
                title="Debt Profile"
                icon={CreditCardIcon}
                accentColor={isDark ? "#EF5350" : "#B71C1C"}
              >
                <InfoRow label="Fuliza Usage Count"        value={n.debt_profile.fuliza_usage_count} />
                <InfoRow label="Fuliza Average Amount"     value={`KSh ${fmt(n.debt_profile.fuliza_avg_amount)}`} />
                <InfoRow label="Fuliza Repayment Behavior" value={n.debt_profile.fuliza_repayment_behavior} />
                <InfoRow label="Other Loan Repayments"     value={`KSh ${fmt(n.debt_profile.other_loan_repayments)}`} highlight />
                <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "text.disabled",
                      mb: 1.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Digital Lenders Detected
                  </Typography>
                  {!n.debt_profile.digital_lenders_detected?.length ? (
                    <AlertBox type="success">
                      None detected — no active mobile loan obligations. This is a positive indicator.
                    </AlertBox>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {n.debt_profile.digital_lenders_detected.map((l, i) => (
                        <Chip
                          key={i}
                          label={l}
                          sx={{
                            bgcolor: isDark ? "rgba(183,28,28,0.22)" : "#FFEBEE",
                            // Explicit color — never inherit from paper bg
                            color: isDark ? "#EF9A9A" : "#B71C1C",
                            fontWeight: 700,
                            fontSize: 12.5,
                            height: 32,
                            borderRadius: 1.5,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </SectionCard>
            )}

            {/* ── Recommendation ── */}
            {cr && (
              <SectionCard
                title="Credit Recommendation"
                icon={AssessmentIcon}
                accentColor={isDark ? "#64B5F6" : "#1565C0"}
              >
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {/* Loan Limit */}
                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        bgcolor: isDark ? "rgba(100,181,246,0.12)" : "#E3F2FD",
                        textAlign: "center",
                        border: "1px solid",
                        borderColor: isDark ? "#1976D2" : "#BBDEFB",
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: isDark ? "#90CAF9" : "#1565C0", fontWeight: 700, mb: 0.5 }}>
                        RECOMMENDED LIMIT
                      </Typography>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: isDark ? "#90CAF9" : "#1565C0", letterSpacing: "-0.02em" }}>
                        KSh {fmt(cr.recommended_loan_limit)}
                      </Typography>
                    </Paper>
                  </Grid>
                  {/* Risk */}
                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        bgcolor: riskT.bg,
                        textAlign: "center",
                        border: "1px solid",
                        borderColor: riskT.border,
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: riskT.color, fontWeight: 700, mb: 0.5, textTransform: "uppercase" }}>
                        {cr.risk_level} RISK
                      </Typography>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: riskT.color, letterSpacing: "-0.02em", textTransform: "capitalize" }}>
                        {cr.risk_level}
                      </Typography>
                    </Paper>
                  </Grid>
                  {/* Weekly repayment */}
                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        bgcolor: isDark ? "rgba(46,125,50,0.15)" : "#E8F5E9",
                        textAlign: "center",
                        border: "1px solid",
                        borderColor: isDark ? "#388E3C" : "#A5D6A7",
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: isDark ? "#66BB6A" : "#1B5E20", fontWeight: 700, mb: 0.5 }}>
                        MAX WEEKLY REPAYMENT
                      </Typography>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: isDark ? "#66BB6A" : "#2E7D32", letterSpacing: "-0.02em" }}>
                        KSh {fmt(cr.max_weekly_repayment)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Installment options */}
                {cr.installment_options?.length > 0 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography
                      sx={{ fontSize: 11, color: "text.disabled", mb: 2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}
                    >
                      Installment Options
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                      {cr.installment_options.map((opt, i) => (
                        <Paper
                          key={i}
                          elevation={0}
                          sx={{
                            p: 2.25,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                            gap: 2,
                            alignItems: "center",
                            transition: "all 0.2s ease",
                            "&:hover": { borderColor: "primary.main", boxShadow: 2 },
                          }}
                        >
                          {[
                            { label: "Term",             val: opt.term_label,            color: "text.primary" },
                            { label: "Loan Amount",      val: `KSh ${fmt(opt.loan_amount)}`,      color: isDark ? "#90CAF9" : "#1565C0" },
                            { label: "Weekly",           val: `KSh ${fmt(opt.weekly_installment)}`, color: "text.primary" },
                            { label: "Total Repayable",  val: `KSh ${fmt(opt.total_repayable)}`,  color: isDark ? "#66BB6A" : "#2E7D32" },
                          ].map(({ label, val, color }) => (
                            <Box key={label}>
                              <Typography sx={{ fontSize: 10, color: "text.disabled", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
                                {label}
                              </Typography>
                              <Typography sx={{ fontSize: 14.5, fontWeight: 600, color }}>
                                {val}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Reasoning */}
                {cr.reasoning && (
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 2.5,
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#F8F9FA",
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1.25, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Reasoning
                    </Typography>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.85, color: "text.primary", fontWeight: 400 }}>
                      {cr.reasoning}
                    </Typography>
                  </Box>
                )}
              </SectionCard>
            )}

            {/* ── Risk Flags ── */}
            {n.risk_flags?.length > 0 && (
              <SectionCard
                title="Risk Flags"
                icon={WarningAmberIcon}
                accentColor={isDark ? "#EF5350" : "#B71C1C"}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {n.risk_flags.map((f, i) => (
                    <AlertBox key={i} type="error">{f}</AlertBox>
                  ))}
                </Box>
              </SectionCard>
            )}

            {/* ── Positive Signals ── */}
            {n.positive_signals?.length > 0 && (
              <SectionCard
                title="Positive Signals"
                icon={CheckCircleOutlineIcon}
                accentColor={isDark ? "#66BB6A" : "#2E7D32"}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {n.positive_signals.map((s, i) => (
                    <AlertBox key={i} type="success">{s}</AlertBox>
                  ))}
                </Box>
              </SectionCard>
            )}

            {/* ── Executive Summary ── */}
            {n.summary_narrative && (
              <SectionCard
                title="Executive Summary"
                icon={SummarizeIcon}
                accentColor={isDark ? "#64B5F6" : "#0277BD"}
              >
                <Typography
                  sx={{
                    fontSize: 14.5,
                    lineHeight: 1.9,
                    color: "text.primary",
                    fontWeight: 400,
                    letterSpacing: "0.01em",
                  }}
                >
                  {n.summary_narrative}
                </Typography>
              </SectionCard>
            )}
          </Box>
        )}

        {/* ── UNKNOWN FORMAT FALLBACK ── */}
        {responseType === "unknown" && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography sx={{ mb: 2, fontWeight: 700, fontSize: 15, color: "text.primary" }}>
              Raw Response Data
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                fontFamily: '"SF Mono",monospace',
                fontSize: 12,
                lineHeight: 1.65,
                bgcolor: isDark ? "rgba(0,0,0,0.3)" : "#F1F3F4",
                p: 3,
                borderRadius: 2,
                overflow: "auto",
                color: "text.primary",
              }}
            >
              {JSON.stringify(data, null, 2)}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default GeneratedNarrativePage;